import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Box, Tabs, Tab, Alert } from '@mui/material';
import MobileNumberInput from '../components/auth/MobileNumberInput';
import OTPVerification from '../components/auth/OTPVerification';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Auth = () => {
  const [authType, setAuthType] = useState('mobile'); // 'mobile' or 'email'
  const [step, setStep] = useState('mobile'); // 'mobile', 'otp', 'login', 'register'
  const [mobileNumber, setMobileNumber] = useState('');
  const [error, setError] = useState('');
  const { generateOTP, verifyOTP, login, register, user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleMobileSubmit = async (number) => {
    try {
      setError('');
      const response = await generateOTP(number);
      setMobileNumber(number);
      setStep('otp');
      toast.success('OTP sent successfully!');
      toast.success(response.otp); // for testing only remove in production
    } catch (error) {
      console.error('Error generating OTP:', error);
      setError(error.response?.data?.message || 'Failed to generate OTP. Please try again.');
      toast.error('Failed to send OTP. Please try again.');
    }
  };

  const handleLogin = async (email, password) => {
    try {
      setError('');
      const result = await login(email, password);
      if (result.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  const handleRegister = async (userData) => {
    try {
      setError('');
      const result = await register(userData);
      if (result.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const handleTabChange = (event, newValue) => {
    setAuthType(newValue);
    setStep(newValue === 'mobile' ? 'mobile' : 'login');
    setError('');
  };

  if (loading) {
    return null; // or a loading spinner
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Welcome
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Please sign in to continue
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs
            value={authType}
            onChange={handleTabChange}
            centered
            sx={{ mb: 3 }}
          >
            <Tab label="Mobile" value="mobile" />
            <Tab label="Email" value="email" />
          </Tabs>

          {authType === 'mobile' ? (
            step === 'mobile' ? (
              <MobileNumberInput onSubmit={handleMobileSubmit} />
            ) : (
              <OTPVerification
                mobileNumber={mobileNumber}
                onVerificationComplete={() => navigate('/dashboard')}
              />
            )
          ) : step === 'login' ? (
            <LoginForm
              onSubmit={handleLogin}
              onRegisterClick={() => setStep('register')}
            />
          ) : (
            <RegisterForm
              onSubmit={handleRegister}
              onLoginClick={() => setStep('login')}
            />
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Auth; 